import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api';
import { AuthContext } from '../Auth/AuthContext';
import { SocketContext } from '../Socket/SocketContext';
import { commercialCardId, getActiveCommercialCards, resolveCommercialSelection } from '../../utils/commercialContext';
import { executeCommercialCommand } from '../../services/commercialCommands';

const Context = createContext(null);
let capabilityRequest;
export const useCommercialCapabilities = () => {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    let active = true;
    if (!capabilityRequest) capabilityRequest = api.get('/commercial-capabilities').then(({ data }) => Boolean(data.enabled)).catch(() => false);
    capabilityRequest.then(value => { if (active) setEnabled(value); });
    return () => { active = false; };
  }, []);
  return enabled;
};
export const useCommercialContext = () => useContext(Context);

export const CommercialContextProvider = ({ ticketId, initialCardId, readonly = false, allowAcceptPendingPreview = false, children }) => {
  const enabled = useCommercialCapabilities();
  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);
  const [cards, setCards] = useState([]);
  const [requestedCardId, setRequestedCardId] = useState(initialCardId || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [canCorrectOutcome, setCanCorrectOutcome] = useState(false);
  const generation = useRef(0);
  const loaded = useRef(false);
  const key = `commercial:${user?.companyId}:${user?.id}:${ticketId}`;
  useEffect(() => {
    generation.current += 1;
    setCards([]);
    loaded.current = false;
    let stored = null;
    try { stored = sessionStorage.getItem(key); } catch (_) { /* Selection still works without storage. */ }
    setRequestedCardId(initialCardId || stored || null);
  }, [key, initialCardId]);
  const refresh = useCallback(async () => {
    if (!enabled || !ticketId) return;
    const current = ++generation.current;
    if (!loaded.current) setLoading(true);
    try {
      const { data } = await api.get(`/tickets/${ticketId}/commercial-context`);
      if (current !== generation.current) return;
      setCards(Array.isArray(data.cards) ? data.cards : []);
      setCanCorrectOutcome(data.canCorrectOutcome === true);
      loaded.current = true;
      setError(null);
    } catch (err) { if (current === generation.current) setError(err); }
    finally { if (current === generation.current) setLoading(false); }
  }, [enabled, ticketId]);
  useEffect(() => { refresh(); return () => { generation.current += 1; }; }, [refresh]);
  useEffect(() => {
    if (!enabled || !ticketId) return undefined;
    let timer;
    const update = data => {
      const changedTicketId = data?.ticketId || data?.ticket?.id || data?.card?.linkedTicketId;
      const previousTicketId = data?.previousTicketId || data?.oldTicketId || data?.sourceTicketId;
      if (changedTicketId && Number(changedTicketId) !== Number(ticketId) && Number(previousTicketId) !== Number(ticketId)) return;
      clearTimeout(timer);
      timer = setTimeout(refresh, 80);
    };
    const local = event => update(event.detail);
    window.addEventListener('commercial:changed', local);
    const socket = user?.companyId && socketManager?.getSocket(user.companyId);
    const channels = [`company-${user?.companyId}-commercial-context`, `company-${user?.companyId}-deal`, `company-${user?.companyId}-kanban-card`, `company-${user?.companyId}-ticket`];
    if (socket) {
      channels.forEach(channel => socket.on(channel, update));
      socket.on('ready', refresh);
    }
    return () => {
      clearTimeout(timer);
      window.removeEventListener('commercial:changed', local);
      if (socket) {
        channels.forEach(channel => socket.off(channel, update));
        socket.off('ready', refresh);
      }
    };
  }, [enabled, ticketId, user?.companyId, socketManager, refresh]);
  const activeCards = useMemo(() => getActiveCommercialCards(cards), [cards]);
  const selectedCard = useMemo(() => {
    const explicit = cards.find(card => commercialCardId(card) === Number(requestedCardId) && !card.archivedAt && (!card.deal || card.deal.state === 'open'));
    if (explicit) return explicit;
    const selected = resolveCommercialSelection(cards, requestedCardId);
    if (selected) return selected;
    const operational = cards.filter(card => !card.dealId && !card.archivedAt);
    return !activeCards.length && operational.length === 1 ? operational[0] : null;
  }, [cards, requestedCardId, activeCards]);
  const selectCard = useCallback(id => {
    setRequestedCardId(id);
    try { sessionStorage.setItem(key, String(id)); } catch (_) { /* Optional session persistence. */ }
  }, [key]);
  const execute = useCallback((type, payload, target = selectedCard) => {
    if (!target) return Promise.reject(new Error('ERR_COMMERCIAL_CONTEXT_REQUIRED'));
    return executeCommercialCommand(type, { ...target }, ticketId, payload).then(async result => { await refresh(); return result; });
  }, [selectedCard, ticketId, refresh]);
  const value = { enabled, cards, activeCards, selectedCard, selectedCardId: commercialCardId(selectedCard), selectCard, loading, error, refresh, execute, ticketId, readonly, allowAcceptPendingPreview, canCorrectOutcome, ambiguous: activeCards.length > 1 && !selectedCard };
  return <Context.Provider value={value}>{children}</Context.Provider>;
};
